import React from 'react';
import SEO from '../components/SEO';
import Layout from '../components/Layout';
import Container from '../components/Container';
import { Desktop, Tablet } from '../constants/responsive';
import { css } from '@emotion/core';
import { FormattedHTMLMessage, useIntl } from 'gatsby-plugin-intl';
import InstitutionsBanner from '../components/InstitutionsBanner';
import Client from '../components/Client';
import InstitutionsForm from '../components/InstitutionsForm';
import InstitutionsAlgorithmIntroduction from '../components/InstitutionsAlgorithmIntroduction';
import InstitutionsStrategyLibrary from '../components/InstitutionsStrategyLibrary';
import InstitutionsService from '../components/InstitutionsService';
import Blank from '../components/Blank';
import GradientBackground from '../components/GradientBackground';
import { graphql, useStaticQuery } from 'gatsby';

const InstitutionsPage = ({ pageContext }) => {
  const intl = useIntl();
  const section = pageContext.name;
  const data = useStaticQuery(graphql`
    query {
      advisory: file(
        relativePath: { eq: "institutions/advisory-video-poster.jpg" }
      ) {
        childImageSharp {
          fluid(maxWidth: 1206) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      smartfund: file(
        relativePath: { eq: "institutions/smartfund-video-poster.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 1206) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
  `);
  const videoNameMap = {
    en: 'en',
    'zh-cn': 'cn',
    'zh-hk': 'cn',
  };
  return (
    <Layout footerForm footerBand={false}>
      <SEO
        title={intl.formatMessage({ id: 'institutions.title' })}
        description={intl.formatMessage({ id: 'institutions.description' })}
        keywords={intl.formatMessage({ id: 'institutions.keywords' })}
      />
      <InstitutionsBanner
        section={section}
        css={css`
          margin-top: ${{
            advisory: '30px',
            selection: '40px',
            smartfund: '40px',
          }[section]};
          ${Tablet} {
            margin-top: 100px;
          }
          ${Desktop} {
            margin-top: 72px;
          }
        `}
      ></InstitutionsBanner>
      {/* 仅下面两个网页有视频 */}
      {section === 'smartfund' && (
        <div
          css={css`
            width: 100%;
            ${Tablet} {
              max-width: 100%;
              margin-top: 10px;
            }
            ${Desktop} {
              max-width: 1206px;
              margin-top: 100px;
            }
          `}
        >
          <video
            className={`advisory-video`}
            css={css`
              width: 100%;
              height: 100%;
            `}
            src={`https://oss-static.aqumon.com/video/aqumon-home-v5/institutions-${section}-video-${
              videoNameMap[intl.locale]
            }.mp4`}
            poster={data[section].childImageSharp.fluid.src}
            muted
            controls
          />
        </div>
      )}

      <Blank height={[100, 100, 110]} />
      <InstitutionsAlgorithmIntroduction section={section} />
      <Blank height={[100, 100, 150]} />
      <InstitutionsStrategyLibrary section={section} />
      <Blank height={[100, 100, 150]} />
      <InstitutionsService section={section} />
      <div
        css={css`
          background: #f6f6f6;
          width: 100%;
          margin-top: 130px;
        `}
      >
        <Container
          css={css`
            display: flex;
            flex-direction: column;
            text-align: center;
            padding-top: 49px;
            padding-bottom: 84px;
            margin: 0 auto;
            ${Tablet} {
              text-align: center;
              align-items: center;
            }
            ${Desktop} {
              align-items: center;
            }
          `}
        >
          <span
            css={css`
              margin-bottom: 20px;
              font-size: 24px;
              font-weight: 500;
              line-height: 1.33;
              ${Tablet} {
                font-size: 36px;
                font-weight: 500;
                line-height: 0.92;
              }
              ${Desktop} {
              }
            `}
          >
            <FormattedHTMLMessage id='institutions.clients.title' />
          </span>
          <span
            css={css`
              font-size: 14px;
              font-weight: normal;
              line-height: 1.71;
              text-align: center;
              ${Tablet} {
                width: 669px;
                font-weight: normal;
                font-size: 18px;
                line-height: 24px;
              }
              ${Desktop} {
                width: 984px;
                font-size: 24px;
                font-weight: normal;
                line-height: 1.38;
              }
            `}
          >
            <FormattedHTMLMessage id='institutions.clients.text' />
          </span>
          <div
            css={css`
              display: flex;
              width: 100%;
              align-items: center;
              justify-content: space-between;
              margin-top: 50px;
              flex-wrap: wrap;
              ${Tablet} {
                justify-content: space-between;
                margin-left: -25px;
                margin-right: -25px;
                width: 750px;
              }
              ${Desktop} {
                justify-content: space-between;
                margin-left: -30px;
                margin-right: -30px;
                width: 1250px;
              }
            `}
          >
            <Client width={[81, 167, 176]} name='boci.png' />

            <Client width={[69, 130, 137]} name='aia.png' />

            <Client width={[90, 240, 240]} name='cmb.png' />
            {/* IPAD、MOBILE端换行 */}
            <hr
              css={css`
                display: block;
                ${Tablet} {
                  height: 34px;
                }
                ${Desktop} {
                  display: none;
                }
              `}
            />
            <Client width={[102, 191, 191]} name='china-amc.png' />
            <Client width={[81, 168, 183]} name='guangzhou.png' />
            {/* WEB端换行 */}
            <hr
              css={css`
                display: none;
                ${Desktop} {
                  display: block;
                }
                height: 33px;
              `}
            />

            <Client width={[81, 171, 171]} name='zhuhai.png' />
            {/* IPAD、MOBILE端换行 */}
            <hr
              css={css`
                display: block;
                ${Tablet} {
                  height: 34px;
                }
                ${Desktop} {
                  display: none;
                }
              `}
            />
            <Client
              width={[80, 177, 167]}
              name={intl.formatMessage({
                id: `institutions.client-logo.ICBC`,
              })}
            />
            <Client width={[73, 154, 167]} name='hua.png' />

            <Client width={[75, 149, 167]} name='citic.png' />
            {/* IPAD、MOBILE端换行 */}
            <hr
              css={css`
                display: block;
                ${Tablet} {
                  height: 34px;
                }
                ${Desktop} {
                  display: none;
                }
              `}
            />
            <Client width={[133, 294, 263]} name='bochk.png' />
            {/* WEB端换行 */}
            <hr
              css={css`
                display: none;
                ${Desktop} {
                  display: block;
                }
                height: 33px;
              `}
            />

            <Client width={[26, 54, 54]} name='ifast.png' />
            <Client width={[108, 260, 260]} name='twosigma.png' />
            {/* IPAD、MOBILE端换行 */}
            <hr
              css={css`
                display: block;
                ${Tablet} {
                  height: 34px;
                }
                ${Desktop} {
                  display: none;
                }
              `}
            />
            <Client width={[73, 173, 173]} name='huatai.png' />
            <Client width={[118, 250, 261]} name='datang.png' />
            <Client width={[74, 176, 176]} name='fangde.png' />
          </div>
        </Container>
      </div>
    </Layout>
  );
};

export default InstitutionsPage;
